import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Button from '@repo/ui/button'
import { DownloadFile, EmailIcon, FamilyThreeIcon, PrintIcon } from '@repo/ui/icons'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { WretchError } from 'wretch/resolver'

import { API, constructApiUrl } from '../../../api/apis.config'
import { acknowledgeEnrollmentRemoval } from '../../../api/centers/acknowledge-enrollment-removal'
import {
  assignHostFamilyToEnrollment,
  changeEnrollmentHostFamily,
  unassignEnrollmentHostFamily
} from '../../../api/centers/assign-host-family-to-enrollment'
import { getCenter } from '../../../api/centers/get-center'
import { getCenterAccomodation } from '../../../api/centers/get-center-accomodation'
import { sendEnrollmentsToHostFamily } from '../../../api/centers/send-enrollments-to-host-family'
import { viewCenterEnrollment } from '../../../api/centers/view-enrollments'
import { getOlPermissions } from '../../../lib/constants/ol-permissions'
import useAuth from '../../../lib/hooks/use-auth'
import { useDataProps } from '../../../lib/hooks/use-data-props'
import { useDownloadDocuments } from '../../../lib/hooks/use-download-documents'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import { AccomodationEnum } from '../../../lib/types/enums/accomodation-type/accomodation-type'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import {
  AssignHostFamilyRequestInput,
  ChangeHostFamilyRequestInput
} from '../../../lib/types/models/center/assign-host-family'
import { CenterBasic } from '../../../lib/types/models/center/center'
import { EnrollmentFilter } from '../../../lib/types/models/enrollment/enrollment-filter'
import {
  EnrollmentCenter,
  EnrollmentDiffusionStateEnum
} from '../../../lib/types/models/enrollment-center/enrollment-center'
import { DataProps } from '../../../lib/types/utils'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import {
  extractAllErrorMessages,
  showAxiosError
} from '../../../lib/utils/functions/misc.functions'
import DynamicDataTable, { DynamicTableRef } from '../../elements/dynamic-table'
import { EnrollmentProfileModel } from '../enrollment-profile/enrollment-profile-model'
import OlCenterAssignHostFamilyForm from '../ol-common/ol-center-placement-enrollment-table/ol-center-assign-host-family-form'
import OlCenterChangeHostFamilyForm from '../ol-common/ol-center-placement-enrollment-table/ol-center-change-host-family-form'
import { getEnrollmentColumns } from '../ol-common/ol-center-placement-enrollment-table/utils'

type Props = {
  olId?: number
  onChangeHostFamilySuccess?: () => void
}

const OlPersonalSpaceCenterStudentDataTable = (props: DataProps<CenterBasic, Props>) => {
  const { id: centerId, data: center, olId } = useDataProps(props, (id) => getCenter(id, 'BASIC'))
  const [newEnrollments, setNewEnrollments] = useState<number[]>([])
  const firstLoadRef = useRef(false)
  const { downloadDocuments } = useDownloadDocuments()
  const olType = useAuth().user?.ol?.type
  const pdfModalManager = useModalManager<'view-pdf', { pdfLink: string; enrollmentId: number }>([
    'view-pdf'
  ])

  const olPermissions = getOlPermissions({ role: useAuth().user?.role, type: olType })

  const tableRef = useRef<DynamicTableRef<EnrollmentCenter>>(null)
  const { t } = useTranslate()
  const [selectedEnrollmentCenter, setSelectedEnrollmentCenter] = useState<number | null>(null)

  const { toggleModal, modalState, selectedData, closeModel } = useModalManager<
    'assign-host-family-to-staff' | 'change-host-family-to-staff',
    { id: number }
  >(['assign-host-family-to-staff', 'change-host-family-to-staff'])

  // A boy and a girl in the same host family: saved only once the user confirms the backend warning
  const [mixedGenderPlacement, setMixedGenderPlacement] = useState<
    | { kind: 'assign'; input: AssignHostFamilyRequestInput; message: string }
    | { kind: 'change'; input: ChangeHostFamilyRequestInput; message: string }
  >()

  const { data: centerAccomodation } = useQuery({
    queryKey: ['center-accomodation', centerId],
    queryFn: () => getCenterAccomodation(centerId)
  })

  const viewCenterEnrollmentMutation = useMutation({
    mutationFn: viewCenterEnrollment,
    onSuccess: (data) => {
      if (data) tableRef.current?.refetch()
    },
    onError: showAxiosError
  })

  const assignHostFamilyToEnrollmentMutation = useMutation({
    mutationFn: assignHostFamilyToEnrollment,
    mutationKey: ['assign-host-family-to-staff'],
    onSuccess: () => {
      toast.success('Host family assigned successfully!')
      setMixedGenderPlacement(undefined)
      tableRef.current?.refetch()
      props.onChangeHostFamilySuccess?.()
    },
    onError: (error, input) => {
      if ((error as WretchError).status === 409 && !input.confirmMixedGender)
        return setMixedGenderPlacement({
          kind: 'assign',
          input,
          message: extractAllErrorMessages(error).join(' ')
        })
      showAxiosError(error)
    }
  })

  const changeEnrollmentHostFamilyMutation = useMutation({
    mutationFn: changeEnrollmentHostFamily,
    mutationKey: ['change-host-family-to-staff'],
    onSuccess: () => {
      toast.success('Host family changed successfully!')
      setMixedGenderPlacement(undefined)
      tableRef.current?.refetch()
      props.onChangeHostFamilySuccess?.()
    },
    onError: (error, input) => {
      if ((error as WretchError).status === 409 && !input.confirmMixedGender)
        return setMixedGenderPlacement({
          kind: 'change',
          input,
          message: extractAllErrorMessages(error).join(' ')
        })
      showAxiosError(error)
    }
  })

  const unassignEnrollmentHostFamilyMutation = useMutation({
    mutationFn: unassignEnrollmentHostFamily,
    mutationKey: ['unassign-host-family-to-staff'],
    onSuccess: () => {
      toast.success('Host family unassigned successfully!')
      tableRef.current?.refetch()
      props.onChangeHostFamilySuccess?.()
    },
    onError: showAxiosError
  })

  const acknowledgeEnrollmentRemovalMutation = useMutation({
    mutationFn: acknowledgeEnrollmentRemoval,
    onSuccess: () => tableRef.current?.refetch(),
    onError: showAxiosError
  })

  const sendEnrollmentsToHostFamilyMutation = useMutation({
    mutationFn: sendEnrollmentsToHostFamily,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      toast.success('Enrollments sent to host family successfully!')
    }
  })

  async function handleProfilePdfClick(enrollmentId: number) {
    const { fileLink } = await downloadDocuments(
      {
        endpoint: constructApiUrl(API.enrollments.exportEnrollmentProfilePdf.url, {
          id: enrollmentId
        })
      },
      { automaticallyDownload: false }
    )

    if (fileLink) pdfModalManager.toggleModal('view-pdf', { pdfLink: fileLink, enrollmentId })
  }

  useEffect(() => {
    if (newEnrollments && newEnrollments.length > 0)
      for (const enrollmentId of newEnrollments)
        viewCenterEnrollmentMutation.mutate({ centerId, enrollmentId })
  }, [newEnrollments])

  const hasHostFamilyAccomodation =
    centerAccomodation?.accomodation === AccomodationEnum.HOST_FAMILY

  if (!center) return null

  return (
    <>
      <DynamicDataTable<EnrollmentCenter, EnrollmentFilter>
        ref={tableRef}
        bodyVariant="white"
        className="bg-white-hover p-0 whitespace-normal break-words"
        columns={[
          ...getEnrollmentColumns({
            t,
            olType,
            userRoles: [RoleEnum.Ol],
            newEnrollmentIds: newEnrollments,
            onNameClick: async (enrollmentCenter) =>
              await handleProfilePdfClick(enrollmentCenter.enrollmentId),
            onCenterGroupChange: () => tableRef.current?.refetch(),
            accomodation: center.accomodation,
            actions: {
              hostFamily: {
                parent1LastName(row) {
                  if (row.hostFamily)
                    toggleModal('change-host-family-to-staff', { id: row.enrollmentId })
                  else toggleModal('assign-host-family-to-staff', { id: row.enrollmentId })
                }
              }
            }
          }),
          {
            accessorKey: 'id',
            header: '',
            cell: ({ row }) => {
              const enrollmentId = row.original.enrollmentId
              if (row.original.diffusionState !== EnrollmentDiffusionStateEnum.DELETED) return null
              return (
                <Button
                  className="border-red w-28 whitespace-normal break-words"
                  variant="destructive"
                  size="small"
                  loading={
                    acknowledgeEnrollmentRemovalMutation.isPending &&
                    row.original.id === selectedEnrollmentCenter
                  }
                  onClick={() => {
                    setSelectedEnrollmentCenter(row.original.id)
                    acknowledgeEnrollmentRemovalMutation.mutate({ centerId, enrollmentId })
                  }}
                >
                  {t('ol.center.enrollment.table.columns.acknowledgement')}
                </Button>
              )
            }
          }
        ]}
        headerType="actions-only"
        version="PARTIAL"
        selectable
        specialRows={[
          {
            color: tailwindColors.green.light.DEFAULT,
            condition: ({ enrollmentId }) => newEnrollments.includes(enrollmentId)
          },
          {
            color: tailwindColors.red.light.DEFAULT,
            condition: ({ diffusionState }) =>
              diffusionState === EnrollmentDiffusionStateEnum.DELETED
          }
        ]}
        allowSearchParam
        endpoint={constructApiUrl(API.centers.getEnrollments.url, { id: centerId })}
        permissions={{ delete: false }}
        actions={[
          {
            type: 'multiple',
            variant: 'primary',
            icon: EmailIcon,
            disabled: (selectedRows) =>
              (selectedRows?.some((row) => !row.hostFamily) || !hasHostFamilyAccomodation) ??
              false,
            hasAccess: olPermissions['send-email-student-info-to-HF'] && hasHostFamilyAccomodation,
            label: t('ol.center.enrollment.table.actions.email.student.info.to.HF'),
            fn: async (data) => {
              await sendEnrollmentsToHostFamilyMutation.mutateAsync({
                centerId,
                ids: data.map(({ id }) => id)
              })
            }
          },
          {
            type: 'multiple',
            icon: DownloadFile,
            variant: 'ghost',
            hasAccess: olPermissions['download-enrollment-id-picture'],
            label: t('ol.center.enrollment.table.actions.download.id.picture'),
            fn: (data) =>
              downloadDocuments({
                ids: data.map(({ enrollmentId }) => enrollmentId),
                endpoint: constructApiUrl(API.centers.printEnrollmentsIdPictures.url, {
                  id: centerId
                })
              })
          },
          {
            type: 'global',
            icon: DownloadFile,
            variant: 'ghost',
            hasAccess: olPermissions['print-enrollments-placement-test-results'],
            label: t('ol.center.enrollment.table.actions.print.placement.test.results'),
            fn: () =>
              downloadDocuments({
                endpoint: constructApiUrl(API.centers.downloadCenterLevelTestsExcel.url, {
                  id: centerId
                })
              })
          },
          {
            type: 'global',
            icon: DownloadFile,
            variant: 'ghost',
            hasAccess: olPermissions['print-enrollments-detailed-test-results'],
            label: t('ol.center.enrollment.table.actions.print.detailed.test.results'),
            fn: () =>
              downloadDocuments({
                endpoint: constructApiUrl(API.centers.printCenterDetailedTestsResults.url, {
                  id: centerId
                })
              })
          },
          {
            type: 'global',
            variant: 'ghost',
            icon: DownloadFile,
            hasAccess: olPermissions['download-enrollments-roaster-list'],
            label: t('ol.center.enrollment.table.actions.download.roaster.list'),
            fn: () =>
              downloadDocuments({
                endpoint: constructApiUrl(API.centers.exportRosterListExcel.url, {
                  id: centerId
                })
              })
          },
          {
            type: 'global',
            icon: PrintIcon,
            variant: 'ghost',
            hasAccess: olPermissions['print-my-placement-list-by-name-of-student'],
            fn: () =>
              downloadDocuments({
                endpoint: constructApiUrl(
                  API.centers.printPdfListOfPlacementsByEnrollmentName.url,
                  { id: centerId }
                )
              }),
            label: t(
              'ol.center.enrollment.table.actions.print.my.placement.list.by.name.of.student'
            )
          },
          {
            type: 'global',
            icon: PrintIcon,
            variant: 'ghost',
            hasAccess:
              olPermissions['print-my-placement-list-by-name-of-FH'] &&
              olPermissions['host-family'] &&
              hasHostFamilyAccomodation,
            label: t('ol.center.enrollment.table.actions.print.my.placement.list.by.name.of.FH'),
            fn: () =>
              downloadDocuments({
                endpoint: constructApiUrl(
                  API.centers.printPdfListOfEnrollmentsPlacementByHostFamily.url,
                  { id: centerId }
                )
              })
          },
          {
            type: 'global',
            icon: PrintIcon,
            variant: 'ghost',
            hasAccess: olPermissions['print-my-student-list-with-health-problem'],
            label: t(
              'ol.center.enrollment.table.actions.print.my.student.list.with.health.problem'
            ),
            fn: () =>
              downloadDocuments({
                endpoint: constructApiUrl(
                  API.centers.printPdfListOfEnrollmentsListWithMedicalCondition.url,
                  { id: centerId }
                )
              })
          }
        ]}
        onDataChange={(data) => {
          if (firstLoadRef.current) return
          firstLoadRef.current = true

          const newIds = data
            .filter(
              (e) => e.diffusionState === EnrollmentDiffusionStateEnum.EXISTING && !e.viewedByOl
            )
            .map((e) => e.enrollmentId)

          setNewEnrollments(newIds)
        }}
      />
      {olId && selectedData && (
        <Modal
          isOpen={modalState['assign-host-family-to-staff']}
          title={t('ol.center.assign.host.family')}
          icon={FamilyThreeIcon}
          size="medium"
          hideActions
          onCancel={() => closeModel('assign-host-family-to-staff')}
        >
          <OlCenterAssignHostFamilyForm
            olId={olId}
            className={MODAL_CONTENT_CLASSNAME}
            buttonsConfig={modalButtonConfig({
              onCancel() {
                closeModel('assign-host-family-to-staff')
              },
              confirmText: t('button.assign'),
              cancelText: t('button.close'),
              onConfirm: async (data) => {
                await assignHostFamilyToEnrollmentMutation.mutateAsync({
                  ...data,
                  centerId,
                  id: selectedData.id
                })
                closeModel('assign-host-family-to-staff')
              }
            })}
          />
        </Modal>
      )}
      {olId && selectedData && (
        <Modal
          isOpen={modalState['change-host-family-to-staff']}
          title={t('ol.center.assign.new.host.family.to.staff')}
          icon={FamilyThreeIcon}
          size="medium"
          hideActions
          onCancel={() => closeModel('change-host-family-to-staff')}
        >
          <OlCenterChangeHostFamilyForm
            olId={olId}
            className={MODAL_CONTENT_CLASSNAME}
            buttonsConfig={modalButtonConfig({
              onCancel() {
                closeModel('change-host-family-to-staff')
              },
              confirmText: t('button.assign'),
              cancelText: t('button.close'),
              onConfirm: async (data) => {
                if (!data.hostFamilyId)
                  await unassignEnrollmentHostFamilyMutation.mutateAsync({
                    centerId,
                    id: selectedData.id,
                    reasonForChange: data.reasonForChange
                  })
                else
                  await changeEnrollmentHostFamilyMutation.mutateAsync({
                    ...data,
                    centerId,
                    id: selectedData.id
                  })
                closeModel('change-host-family-to-staff')
              }
            })}
          />
        </Modal>
      )}

      <Modal
        variant="confirmation"
        isOpen={!!mixedGenderPlacement}
        title={t('ol.center.host.family.mixed.gender.title')}
        icon={FamilyThreeIcon}
        actionButtonsOptions={{
          confirmText: t('button.confirm'),
          cancelText: t('button.cancel'),
          onConfirm: () => {
            if (mixedGenderPlacement?.kind === 'assign')
              assignHostFamilyToEnrollmentMutation.mutate(
                { ...mixedGenderPlacement.input, confirmMixedGender: true },
                { onSuccess: () => closeModel('assign-host-family-to-staff') }
              )
            if (mixedGenderPlacement?.kind === 'change')
              changeEnrollmentHostFamilyMutation.mutate(
                { ...mixedGenderPlacement.input, confirmMixedGender: true },
                { onSuccess: () => closeModel('change-host-family-to-staff') }
              )
          }
        }}
        isLoading={
          assignHostFamilyToEnrollmentMutation.isPending ||
          changeEnrollmentHostFamilyMutation.isPending
        }
        onCancel={() => setMixedGenderPlacement(undefined)}
      >
        <div className="px-5 pt-3 text-center">
          <Typography variant="secondary-body">{mixedGenderPlacement?.message}</Typography>
        </div>
      </Modal>

      {pdfModalManager.selectedData && (
        <EnrollmentProfileModel
          id={pdfModalManager.selectedData.enrollmentId}
          src={pdfModalManager.selectedData.pdfLink}
          onEnrollmentUpdate={() => tableRef.current?.refetch()}
          isOpen={pdfModalManager.modalState['view-pdf']}
          onCancel={() => pdfModalManager.closeModel('view-pdf')}
        />
      )}
    </>
  )
}

export default OlPersonalSpaceCenterStudentDataTable
